import { useState, useEffect } from "react";

export const Gallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const previewCount = 8;

  // Load the images from your specific file structure
  useEffect(() => {
    // This loads the actual images from your file structure
    const galleryImages = Array.from({ length: 20 }, (_, i) => {
      const imageNumber = i + 1;
      const extension = imageNumber === 1 ? '.JPG' : '.jpg';
      return {
        id: imageNumber,
        src: `img/gallery/gallery (${imageNumber})${extension}`,
        thumbnail: `img/gallery/gallery (${imageNumber})${extension}`,
        alt: `Gallery image ${imageNumber}`,
        caption: `Image ${imageNumber}`
      };
    });

    setImages(galleryImages);
    setIsLoading(false);
  }, []);

  const openLightbox = (index) => {
    setActiveIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setActiveIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const toggleFullGallery = () => {
    setShowAllGallery(!showAllGallery);
    // Scroll to gallery section when expanding
    if (!showAllGallery) {
      setTimeout(() => {
        document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;

      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  if (isLoading) {
    return (
      <div className="gallery-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Display either preview or all images based on state
  const displayImages = showAllGallery ? images : images.slice(0, previewCount);

  return (
    <div id="portfolio" className="text-center">
      <div className="container">
        <div className="section-title">
          <h2>Gallery</h2>
          <div className="title-underline"></div>
          <p className="section-subtitle">
            Highlights from our projects and events
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="gallery-grid">
          {displayImages.map((image, index) => (
            <div
              key={image.id}
              className="gallery-item"
              onClick={() => openLightbox(showAllGallery ? index : index)}
            >
              <img
                src={image.thumbnail}
                alt={image.alt}
                className="gallery-image"
              />
              <div className="gallery-overlay">
                <p className="gallery-caption">{image.caption}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Toggle Button */}
        {images.length > previewCount && (
          <div className="gallery-actions">
            <button
              onClick={toggleFullGallery}
              className="btn btn-primary gallery-toggle-btn"
            >
              {showAllGallery ? 'Show Less' : 'View All Photos'}
              {!showAllGallery && <span className="arrow-icon">→</span>}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox">
          <button
            onClick={closeLightbox}
            className="lightbox-close"
            aria-label="Close lightbox"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <button
            onClick={goToPrevious}
            className="lightbox-nav lightbox-prev"
            aria-label="Previous image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div className="lightbox-content">
            <div className="lightbox-image-container">
              <img
                src={images[activeIndex].src}
                alt={images[activeIndex].alt}
                className="lightbox-image"
              />
              <div className="lightbox-caption">
                <p className="caption-text">{images[activeIndex].caption}</p>
                <p className="caption-counter">{activeIndex + 1} / {images.length}</p>
              </div>
            </div>
          </div>

          <button
            onClick={goToNext}
            className="lightbox-nav lightbox-next"
            aria-label="Next image"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};